import React from 'react';
import { occasionQuestions } from './occasionQuestions';

// Per-occasion visual identity. Pairs an emoji glyph with a soft accent
// gradient so the picker reads as a row of inviting tiles instead of a
// list of plain text buttons.
const OCCASION_META = {
  Wedding: { emoji: '💒', accent: '#ff80a8', tint: 'rgba(255, 128, 168, 0.18)' },
  'New Baby': { emoji: '👶', accent: '#7cc7ff', tint: 'rgba(124, 199, 255, 0.18)' },
  Anniversary: { emoji: '💞', accent: '#ff7a7a', tint: 'rgba(255, 122, 122, 0.18)' },
  'Celebration of Life': { emoji: '🌟', accent: '#ffd166', tint: 'rgba(255, 209, 102, 0.18)' },
  'Vacation Recap': { emoji: '✈️', accent: '#5dd6c1', tint: 'rgba(93, 214, 193, 0.18)' },
  'Preserving Memories': { emoji: '📦', accent: '#f9a26c', tint: 'rgba(249, 162, 108, 0.18)' },
  'Birthday Party': { emoji: '🎂', accent: '#c79bff', tint: 'rgba(199, 155, 255, 0.18)' },
  'Family Storytelling': { emoji: '📖', accent: '#9bd17b', tint: 'rgba(155, 209, 123, 0.18)' },
  'Other Milestone': { emoji: '🎯', accent: '#a4adc2', tint: 'rgba(164, 173, 194, 0.18)' },
};
const FALLBACK_META = { emoji: '✨', accent: '#a4adc2', tint: 'rgba(164, 173, 194, 0.18)' };

// Single source of truth for the occasion picker modal — used by the
// contributor flow (`/gift?test=upload`) and the receiver flow
// (`/gift-content`). Inline-styled so it doesn't depend on the host page's
// css module.
const OccasionPickerModal = ({ open, onPick, onClose, hint }) => {
  if (!open) return null;
  const occasions = Object.keys(occasionQuestions);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pick an occasion"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'rgba(8, 6, 18, 0.86)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'occasionFadeIn 0.2s ease both',
      }}
    >
      <style>
        {`
          @keyframes occasionFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes occasionPopIn {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          /* Mobile-friendly tweaks for the picker on phones. */
          @media (max-width: 720px) {
            .occasionPickerCard { padding: 20px 16px !important; border-radius: 16px !important; }
            .occasionPickerGrid {
              grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)) !important;
              grid-auto-rows: 104px !important;
              gap: 10px !important;
            }
            .occasionPickerTitle { font-size: 1.15rem !important; }
            .occasionPickerSubtitle { font-size: 0.8rem !important; }
            .occasionPickerEmoji { width: 38px !important; height: 38px !important; font-size: 1.3rem !important; }
            .occasionPickerLabel { font-size: 0.82rem !important; }
          }
          @media (max-width: 380px) {
            .occasionPickerCard { padding: 16px 12px !important; border-radius: 14px !important; }
            .occasionPickerGrid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              grid-auto-rows: 92px !important;
              gap: 8px !important;
            }
            .occasionPickerTitle { font-size: 1rem !important; }
            .occasionPickerEmoji { width: 32px !important; height: 32px !important; font-size: 1.1rem !important; border-radius: 10px !important; }
            .occasionPickerLabel { font-size: 0.76rem !important; }
          }
        `}
      </style>
      <div
        className="occasionPickerCard"
        style={{
          width: '100%',
          maxWidth: '620px',
          background: 'linear-gradient(160deg, rgba(50, 56, 72, 0.96), rgba(28, 32, 42, 0.98))',
          borderRadius: '22px',
          padding: '26px 24px 22px 24px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
          color: '#fff',
          fontFamily: 'inherit',
          animation: 'occasionPopIn 0.25s cubic-bezier(0.34, 1.4, 0.64, 1) both',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '6px',
        }}
        >
          <div>
            <h2
              className="occasionPickerTitle"
              style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}
            >
              What&apos;s the occasion?
            </h2>
            <p
              className="occasionPickerSubtitle"
              style={{
                margin: '4px 0 0 0',
                color: '#9aa0b5',
                fontSize: '0.88rem',
                lineHeight: 1.45,
              }}
            >
              {hint || 'Pick one — we\'ll show you 5 short prompts to record from.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.04)',
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            ✕
          </button>
        </div>

        <div
          className="occasionPickerGrid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            // Lock every row to the same height regardless of content. Without
            // this, grid auto-sizes rows by content + button's `height` can be
            // overridden by the parent layout, producing the uneven rows.
            gridAutoRows: '120px',
            alignItems: 'stretch',
            gap: '12px',
            marginTop: '18px',
          }}
        >
          {occasions.map((occ) => {
            const meta = OCCASION_META[occ] || FALLBACK_META;
            return (
              <button
                key={occ}
                type="button"
                onClick={() => onPick(occ)}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  height: '100%', // fill the grid cell (locked at 120px above)
                  boxSizing: 'border-box',
                  padding: '14px 12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '14px',
                  background: `linear-gradient(160deg, ${meta.tint}, rgba(255,255,255,0.03))`,
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  fontSize: '0.92rem',
                  lineHeight: 1.25,
                  textAlign: 'center',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  transition: 'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = meta.accent;
                  e.currentTarget.style.boxShadow = `0 12px 28px ${meta.tint}, 0 0 0 1px ${meta.accent} inset`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span
                  aria-hidden="true"
                  className="occasionPickerEmoji"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '46px',
                    height: '46px',
                    borderRadius: '14px',
                    background: 'rgba(0,0,0,0.32)',
                    border: `1px solid ${meta.accent}55`,
                    fontSize: '1.55rem',
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  {meta.emoji}
                </span>
                <span
                  className="occasionPickerLabel"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {occ}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OccasionPickerModal;
